import React, { useEffect, useMemo, useState } from 'react';
import { snakeCase } from 'lodash';

import Button from '../../library/button/button';
import Tooltip from '../../library/tooltip/tooltip';
import Typography from '../../library/typography/typography';
import {
  CarBumpRegularIcon,
  CarTrafficLightRegularIcon,
  ChargingStationRegularIcon,
  ClockRegularIcon,
  CloudShowersHeavyRegularIcon,
  GasPumpRegularIcon,
  PiggyBankRegularIcon,
  RoadRegularIcon,
  SnowSkidControlRegularIcon,
  SteeringWheelRegularIcon,
  SunBrightRegularIcon,
  VolumeRegularIcon,
  WetRoadsRegularIcon
} from '../../shared/icons/icons';
import CirclesRating from '../circles-rating/circles-rating';

import {
  useAemContent,
  useFitment,
  useTreadwellPreferences
} from '../../shared/hooks/hooks';

import { formatMilesInThousands } from '../../shared/utils/format/format';
import { isWinterTire } from '../../shared/utils/product/product';
import { calculateLifeExpectancy } from '../../shared/utils/treadwell/treadwell';

import {
  CmsPromotionBannerComponent,
  CmsToolTips,
  CmsToolTipsComponent,
  FuelTypeEnum,
  Maybe,
  ProductData,
  TreadWellPriorityEnum
} from '../../shared/types/graphql-types';
import { PriorityDetails } from '../../shared/types/product';
import { AEM_CONTENT_PAGE_IDS } from '../../shared/constants/cms';
import { TREADWELL_PRIORITY } from '../../shared/constants/treadwell';

import './product-first-priority-details.scss';

enum PriorityEnum {
  NoRollingResistance = 'noRollingResistance',
  WinterTires = 'winterTires'
}

interface AemContentTypes {
  evRange: Maybe<CmsToolTips>;
  evRangePending: Maybe<CmsToolTips>;
  fuelEfficiency: Maybe<CmsToolTips>;
  fuelEfficiencyPending: Maybe<CmsToolTips>;
  handlingSteeringResponse: Maybe<CmsToolTips>;
  milesPerDollar: Maybe<CmsToolTips>;
  skidControl: Maybe<CmsToolTips>;
  stoppingDistance: Maybe<CmsToolTips>;
  yearsAndMonths: Maybe<CmsToolTips>;
}

interface ProductFirstPriorityDetailsProps {
  dataReportUrl?: string;
  hideLink?: boolean;
  onClick?: () => void;
  product: ProductData;
}

function ProductFirstPriorityDetails(props: ProductFirstPriorityDetailsProps) {
  const {
    dataReportUrl = 'https://www.discounttire.com/404',
    hideLink = false,
    onClick,
    product
  } = props;

  const { isTreadwellFlow, treadwellPreferences } = useTreadwellPreferences();

  const { aemTooltipsContent } = useAemContent(
    AEM_CONTENT_PAGE_IDS.treadwellPdp
  );

  const contentMap: AemContentTypes = useMemo(() => {
    const priorityKeys = {
      evRange: null,
      evRangePending: null,
      fuelEfficiency: null,
      fuelEfficiencyPending: null,
      handlingSteeringResponse: null,
      milesPerDollar: null,
      skidControl: null,
      stoppingDistance: null,
      yearsAndMonths: null
    };

    if (Object.keys(aemTooltipsContent).length > 0) {
      Object.keys(priorityKeys).forEach(key => {
        const tooltipContent = aemTooltipsContent[snakeCase(key)];

        if (tooltipContent) {
          priorityKeys[key] = tooltipContent;
        }
      });
    }

    return priorityKeys;
  }, [aemTooltipsContent]);

  const normalizedTreadwellPreferences: {
    [key: string]: CmsPromotionBannerComponent;
  } = useMemo(
    () =>
      treadwellPreferences?.selectedCustomDrivingPriorities?.reduce(
        (acc, priority) => {
          if (priority) {
            acc[priority.id] = priority;
          }
          return acc;
        },
        {}
      ) || {},
    [treadwellPreferences]
  );

  const { selectedFitment } = useFitment();
  const isEV = selectedFitment?.fuelType === FuelTypeEnum.Bev;

  const { dryRating, rollingResistanceRating, wetRating } =
    product?.recommendationData?.stopping || product?.tdsData?.stopping || {};
  const { comfort, handling, quiet, wetHandling } =
    product?.recommendationData?.rideRating ||
    product?.tdsData?.rideRating ||
    {};
  const { median: medianMileage, min: minMileage } =
    product?.recommendationData?.mileage || product?.tdsData?.mileage || {};

  const { snowBrakeFeet, snowBrakeRating, snowHandlingRating } =
    product?.recommendationData?.winter || product?.tdsData?.winter || {};

  const { tireLifeMonths, tireLifeYears } = calculateLifeExpectancy(
    product,
    15 * 1000,
    15 * 1000
  );
  const { milesPerDollar } = product?.recommendationData || {};

  const actualPrice = product?.mapPrice?.value ?? product?.price?.value;
  const actualMilesPerDollar =
    milesPerDollar ?? Math.round((medianMileage || 0) / (actualPrice || 1));

  const priorityDetailsArr: PriorityDetails[] = useMemo(
    () => [
      {
        id: TreadWellPriorityEnum.StoppingDistance,
        ratingDescription1: (
          <div>
            <SunBrightRegularIcon styleName="icon" />
            Dry Road Rating
          </div>
        ),
        ratingDescription2: (
          <div>
            <CloudShowersHeavyRegularIcon styleName="icon" />
            Wet Road Rating
          </div>
        ),
        ratingValue1: dryRating,
        ratingValue2: wetRating,
        title:
          normalizedTreadwellPreferences[TREADWELL_PRIORITY.STOPPING_DISTANCE]
            ?.name || 'Stopping & Traction',
        tooltip:
          normalizedTreadwellPreferences[TREADWELL_PRIORITY.STOPPING_DISTANCE]
            ?.imageAltText ||
          'How well tires grip in dry, wet and winter weather.'
      },
      {
        id: TreadWellPriorityEnum.ComfortAndNoise,
        ratingDescription1: (
          <div>
            <VolumeRegularIcon styleName="icon" />
            Quiet Ride
          </div>
        ),
        ratingDescription2: (
          <div>
            <CarBumpRegularIcon styleName="icon" />
            Comfort
          </div>
        ),
        ratingValue1: quiet,
        ratingValue2: comfort,
        title:
          normalizedTreadwellPreferences[TREADWELL_PRIORITY.COMFORT_AND_NOISE]
            ?.name,
        tooltip:
          normalizedTreadwellPreferences[TREADWELL_PRIORITY.COMFORT_AND_NOISE]
            ?.imageAltText
      },
      {
        id: TreadWellPriorityEnum.FuelEfficiency,
        ratingDescription1: (
          <div>
            {isEV ? (
              <ChargingStationRegularIcon styleName="icon" />
            ) : (
              <GasPumpRegularIcon styleName="icon" />
            )}
            {isEV
              ? contentMap.evRange?.title
              : contentMap.fuelEfficiency?.title}
          </div>
        ),
        ratingDescription2: (
          <div>
            <VolumeRegularIcon styleName="icon" />
            Quiet Ride
          </div>
        ),
        ratingValue1: rollingResistanceRating,
        ratingValue2: quiet,
        title: isEV
          ? contentMap.evRange?.title
          : contentMap.fuelEfficiency?.title,
        tooltip: isEV
          ? contentMap.evRange?.tipText
          : contentMap.fuelEfficiency?.tipText
      },
      {
        id: TreadWellPriorityEnum.Handling,
        ratingDescription1: (
          <div>
            <SteeringWheelRegularIcon styleName="icon" />
            Steering Response
          </div>
        ),
        ratingDescription2: (
          <div>
            <WetRoadsRegularIcon styleName="icon" />
            Wet Handling Response
          </div>
        ),
        ratingValue1: handling,
        ratingValue2: wetHandling,
        title: 'Steering Precision',
        tooltip: contentMap.handlingSteeringResponse?.tipText
      },
      {
        id: TreadWellPriorityEnum.LifeOfTire,
        noRatingDescription1: (
          <Typography styleName="sub-description" tag="div">
            <div>
              <ClockRegularIcon styleName="icon" />
              Life of Tire
            </div>
            <div>
              <strong>{tireLifeYears}</strong>yr{' '}
              <strong>{tireLifeMonths}</strong>mo
            </div>
          </Typography>
        ),
        noRatingDescription2: actualMilesPerDollar && (
          <Typography styleName="sub-description" tag="div">
            <div>
              <PiggyBankRegularIcon styleName="icon" />
              {contentMap.milesPerDollar?.title}
            </div>
            <div>
              <strong>{actualMilesPerDollar}</strong>
            </div>
          </Typography>
        ),
        title:
          normalizedTreadwellPreferences[TREADWELL_PRIORITY.LIFE_OF_TIRE]?.name,
        tooltip:
          normalizedTreadwellPreferences[TREADWELL_PRIORITY.LIFE_OF_TIRE]
            ?.imageAltText
      },
      {
        id: PriorityEnum.NoRollingResistance,
        pendingMsg: (
          <div>
            {isEV ? (
              <ChargingStationRegularIcon styleName="icon" />
            ) : (
              <GasPumpRegularIcon styleName="icon" />
            )}
            {isEV
              ? contentMap.evRangePending?.title
              : contentMap.fuelEfficiencyPending?.title}
            <Tooltip
              content={
                isEV
                  ? contentMap.evRangePending?.tipText
                  : contentMap.fuelEfficiencyPending?.tipText
              }
              styleName="tooltip"
            />
          </div>
        ),
        ratingDescription2: (
          <div>
            <VolumeRegularIcon styleName="icon" />
            Quiet Ride
          </div>
        ),
        ratingValue2: quiet,
        title: isEV
          ? contentMap.evRangePending?.title
          : contentMap.fuelEfficiencyPending?.title,
        tooltip: isEV
          ? contentMap.evRangePending?.tipText
          : contentMap.fuelEfficiencyPending?.tipText
      },
      {
        id: PriorityEnum.WinterTires,
        ratingDescription1: (
          <div>
            <SnowSkidControlRegularIcon styleName="icon" />
            {contentMap.skidControl?.title || 'Snow Skid Control'}
            {contentMap.skidControl?.tipText && (
              <Tooltip
                content={contentMap.skidControl.tipText}
                styleName="tooltip"
              />
            )}
          </div>
        ),
        ...(snowBrakeRating != null
          ? {
              ratingDescription2: (
                <div>
                  <CarTrafficLightRegularIcon styleName="icon" />
                  {contentMap.stoppingDistance?.title ||
                    'Snow Stopping Distance'}
                  {contentMap.stoppingDistance?.tipText && (
                    <Tooltip
                      content={contentMap.stoppingDistance.tipText}
                      styleName="tooltip"
                    />
                  )}
                </div>
              ),
              ratingValue2: snowBrakeRating
            }
          : {
              noRatingDescription2: (
                <Typography styleName="sub-description" tag="div">
                  <div>
                    <CarTrafficLightRegularIcon styleName="icon" />
                    {contentMap.stoppingDistance?.title ||
                      'Snow Stopping Distance'}
                    {contentMap.stoppingDistance?.tipText && (
                      <Tooltip
                        content={contentMap.stoppingDistance.tipText}
                        styleName="tooltip"
                      />
                    )}
                  </div>
                  <div>{snowBrakeFeet} Feet</div>
                </Typography>
              )
            }),
        ratingValue1: snowHandlingRating,
        title: 'Snow & Ice',
        tooltip:
          'Winter includes ice, slush, snow and temperatures consistently below 45F'
      }
    ],
    [
      actualMilesPerDollar,
      comfort,
      contentMap,
      dryRating,
      handling,
      isEV,
      normalizedTreadwellPreferences,
      quiet,
      rollingResistanceRating,
      snowBrakeFeet,
      snowBrakeRating,
      snowHandlingRating,
      tireLifeMonths,
      tireLifeYears,
      wetHandling,
      wetRating
    ]
  );
  const [detailsObj, setDetailsObj] = useState<PriorityDetails>(
    priorityDetailsArr[0]
  );

  useEffect(() => {
    function getNumber1PriorityId() {
      let priorityId: TreadWellPriorityEnum | PriorityEnum =
        isWinterTire(product) && snowBrakeFeet && snowHandlingRating
          ? PriorityEnum.WinterTires
          : TreadWellPriorityEnum.StoppingDistance;

      if (treadwellPreferences) {
        if (
          treadwellPreferences.showWinterTires &&
          snowBrakeFeet &&
          snowHandlingRating
        ) {
          priorityId = PriorityEnum.WinterTires;
        } else if (treadwellPreferences.isCustomDriverTypeShowing) {
          const customDriverType =
            treadwellPreferences.selectedCustomDrivingPriorities as CmsPromotionBannerComponent[];
          priorityId = customDriverType[0].id as TreadWellPriorityEnum;
        } else {
          const driverType =
            treadwellPreferences.selectedDriverType as CmsToolTipsComponent;
          const tooltips = driverType?.tooltips as CmsToolTips[];
          priorityId = tooltips?.[0]?.id as TreadWellPriorityEnum;
        }
      }

      if (
        priorityId === TreadWellPriorityEnum.FuelEfficiency &&
        rollingResistanceRating === 0
      ) {
        priorityId = PriorityEnum.NoRollingResistance;
      }

      return priorityId;
    }

    const detailsObj = priorityDetailsArr.filter(
      item => item.id === getNumber1PriorityId()
    )[0];

    setDetailsObj(detailsObj);
  }, [
    isEV,
    priorityDetailsArr,
    product,
    rollingResistanceRating,
    snowBrakeFeet,
    snowHandlingRating,
    treadwellPreferences
  ]);

  if (!detailsObj) {
    return null;
  }

  return (
    <div className="product-first-priority-details" styleName="container">
      <div styleName="header">
        <div styleName="treadwell-logo" />
        <Typography styleName="title" tag="span">
          {isTreadwellFlow && !treadwellPreferences?.showWinterTires
            ? "Why It's a Match"
            : detailsObj.title}
          <Tooltip
            content={
              isTreadwellFlow && !treadwellPreferences?.showWinterTires ? (
                <>
                  Based on your top priority as&nbsp;
                  <strong>{detailsObj.title}</strong>, we found tires that score
                  highly in categories you care about.
                </>
              ) : (
                detailsObj.tooltip
              )
            }
            styleName="tooltip"
          />
        </Typography>
      </div>
      <div styleName="footer">
        {detailsObj.pendingMsg && (
          <Typography styleName="sub-description" tag="div">
            {detailsObj.pendingMsg}
          </Typography>
        )}
        {Boolean(detailsObj.ratingValue1) && (
          <Typography styleName="sub-description" tag="div">
            {detailsObj.ratingDescription1}
            <CirclesRating rating={detailsObj.ratingValue1} />
          </Typography>
        )}
        {Boolean(detailsObj.ratingValue2) && (
          <Typography styleName="sub-description" tag="div">
            {detailsObj.ratingDescription2}
            <CirclesRating rating={detailsObj.ratingValue2} />
          </Typography>
        )}
        {detailsObj.noRatingDescription1}
        {detailsObj.noRatingDescription2}
        {Boolean(medianMileage) && (
          <Typography styleName="sub-description" tag="div">
            <div>
              <RoadRegularIcon styleName="icon" />
              Tread Life Estimate
            </div>
            <div>
              {formatMilesInThousands(minMileage)}-
              {formatMilesInThousands(medianMileage)}
            </div>
          </Typography>
        )}
        {!hideLink && (
          <Button
            href={`${dataReportUrl}#treadwell-data`}
            onClick={onClick}
            size="regular"
            styleName="button"
          >
            view full report
          </Button>
        )}
      </div>
    </div>
  );
}

export default ProductFirstPriorityDetails;
